/**
 * `/admin#roles` - the seven predefined roles, what each allows, who holds it,
 * and the role new people start with.
 *
 * Roles are built in and not editable (migration `0042`); they are assigned on
 * Users and Groups. This screen answers "what does Argus Operator mean?" and
 * "who has it?" without anybody reading permission keys, and holds the one
 * setting that is about roles themselves: the new-user default, which applies to
 * future accounts only. See `docs/plans/access-management.md`.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, catalogAvailable, catalogAvailabilityKnown, isRootUser, myPermissions } from '../../state/store.ts';
import { getRoleDetail, setDefaultRole } from '../../api/endpoints.ts';
import type { RoleDetail, RoleSummary } from '../../api/types.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import { showToast } from '../../ui/toast.ts';
import { userMessage } from '../../api/errors.ts';
import { loadRoleCatalog, openAdminGroup, openAdminUser, roleCatalog, roleLabel, rolesOfKind } from './role-catalog.ts';

const KIND_LABEL: Record<string, string> = {
  base: 'Explorer role',
  module: 'Optional',
  admin: 'Root assigns',
};

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

@customElement('roles-panel')
export class RolesPanel extends LightElement {
  private store = new StoreController(this, [myPermissions, isRootUser, catalogAvailable, catalogAvailabilityKnown, roleCatalog]);
  @state() private loading = false;
  @state() private error = '';
  @state() private openName: string | null = null;
  @state() private detail: RoleDetail | null = null;
  @state() private saving = false;
  private requested = false;

  override willUpdate(): void {
    if (!this.requested && canDo('users:read') && catalogAvailabilityKnown.value && catalogAvailable.value) {
      this.requested = true;
      void this.load();
    }
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    const catalog = await loadRoleCatalog(true);
    if (!catalog) this.error = 'The roles could not be read.';
    this.loading = false;
  }

  private async open(role: RoleSummary): Promise<void> {
    if (this.openName === role.name) {
      this.openName = null;
      this.detail = null;
      return;
    }
    this.openName = role.name;
    this.detail = null;
    try {
      const detail = await getRoleDetail(role.name);
      if (this.openName === role.name) this.detail = detail;
    } catch (e) {
      showToast('error', userMessage(e));
      if (this.openName === role.name) this.openName = null;
    }
  }

  private async changeDefault(role: string): Promise<void> {
    const current = roleCatalog.value?.default_role;
    if (!role || role === current || this.saving) return;
    const ok = await confirmAction({
      title: `Start new people as ${roleLabel(role)}?`,
      message: `Everyone who signs in for the first time from now on gets ${roleLabel(role)}. Nobody who already has an account changes - assign them on Users or through a group.`,
      confirmLabel: 'Change default',
      destructive: false,
    });
    if (!ok) return;
    this.saving = true;
    try {
      roleCatalog.set(await setDefaultRole(role));
      showToast('success', `New people now start as ${roleLabel(role)}`);
    } catch (e) {
      showToast('error', userMessage(e));
    } finally {
      this.saving = false;
    }
  }

  private renderDefault(): TemplateResult {
    const current = roleCatalog.value?.default_role ?? '';
    return html`<div class="roles-default">
      <label>New people start as
        <select class="share-role" aria-label="Role for new people" .value=${current}
          ?disabled=${!canDo('users:write') || this.saving}
          @change=${(e: Event) => { const select = e.target as HTMLSelectElement; const value = select.value;
            select.value = current; void this.changeDefault(value); }}>
          ${rolesOfKind('base').map((r) => html`<option value=${r.name} ?selected=${r.name === current}>${r.display_name}</option>`)}
        </select>
      </label>
      <p class="admin-note">Future users only - nobody who exists already changes. SONAR and Argus are never given automatically: assign their roles, or open them to more people in Features.</p>
    </div>`;
  }

  private renderDetail(role: RoleSummary): TemplateResult {
    const detail = this.detail?.name === role.name ? this.detail : null;
    if (!detail) return html`<p class="admin-note" role="status">Loading the role…</p>`;
    return html`<div class="people-detail">
      <div class="people-access groups-access">
        <div class="people-controls">
          <h2>Allows</h2>
          <ul class="groups-capabilities">${detail.permissions.map((p) => html`<li>${p.label}</li>`)}</ul>
          ${detail.includes.length ? html`<p class="admin-note">Includes ${detail.includes.map(roleLabel).join(' and ')} - assign this one role, not both.</p>` : nothing}
          <details class="access-technical"><summary>Technical details</summary>
            <div class="admin-perm-list">${detail.permissions.map((p) => html`<code class="admin-perm">${p.name}</code>`)}</div>
          </details>
        </div>
        <div class="people-sources">
          <h2>Assigned directly <span class="people-muted groups-inline">${plural(detail.users.length, 'person', 'people')}</span></h2>
          ${detail.users.length ? html`<ul class="people-binding-list">${detail.users.map((u) => html`<li>
            <button class="people-name" @click=${() => openAdminUser(u.user_id)}>${u.display_name}</button>
            <span class="people-muted">${u.email ?? ''}${u.granted_via === 'onboarding' ? ' · new-user default' : u.granted_via === 'migration' ? ' · kept when roles were introduced' : u.granted_by_name ? ` · by ${u.granted_by_name}` : ''}</span>
          </li>`)}</ul>` : html`<p class="admin-note">Nobody holds it directly.</p>`}
          <h2>Through groups</h2>
          ${detail.group_assignments.length ? html`<ul class="people-binding-list">${detail.group_assignments.map((g) => html`<li>
            <button class="people-name" @click=${() => openAdminGroup(g.group_id)}>${g.name}</button>
            <span class="people-muted">${plural(g.member_count, 'member')}${g.granted_by_name ? ` · by ${g.granted_by_name}` : ''}</span>
          </li>`)}</ul>` : html`<p class="admin-note">No group carries it.</p>`}
        </div>
      </div>
    </div>`;
  }

  private renderMatrix(): TemplateResult {
    const catalog = roleCatalog.value!;
    const groups = [...new Set(catalog.capabilities.map((c) => c.group))];
    return html`<div class="people-table-wrap roles-matrix-wrap"><table class="people-table roles-matrix">
      <thead><tr><th scope="col">Capability</th>${catalog.roles.map((r) => html`<th scope="col" title=${r.description ?? ''}>${r.display_name}</th>`)}</tr></thead>
      ${groups.map((group) => html`<tbody>
        <tr class="access-group"><th scope="rowgroup" colspan=${catalog.roles.length + 1}>${group}</th></tr>
        ${catalog.capabilities.filter((c) => c.group === group).map((c) => html`<tr>
          <th scope="row">${c.label}${c.note ? html`<span class="people-muted">${c.note}</span>` : nothing}</th>
          ${c.features_only ? html`<td colspan=${catalog.roles.length} class="roles-matrix-features">Everyone Features lets in - no role needed</td>` : catalog.roles.map((r) => {
            const on = c.roles.includes(r.name);
            return html`<td class=${on ? 'is-allowed' : ''} aria-label=${`${r.display_name}: ${on ? 'yes' : c.root_only ? 'Root only' : 'no'}`}>
              ${on ? '✓' : c.root_only ? html`<span class="people-muted">Root</span>` : html`<span class="people-muted">–</span>`}</td>`;
          })}
        </tr>`)}
      </tbody>`)}
    </table></div>
    <p class="admin-note people-help">Features comes first: a feature switched off, or with an audience that leaves someone out, blocks what their roles allow - Administrator and Root included. An explicit Features audience can let people into SONAR or Argus without a role, but never grants Argus actions.</p>`;
  }

  override render(): TemplateResult {
    if (!catalogAvailabilityKnown.value) return html`<div class="catalog-empty" role="status">Loading roles…</div>`;
    if (!catalogAvailable.value) return html`<div class="catalog-empty"><p class="catalog-empty-title">Roles need the metadata database</p><p>The database is not configured or could not be reached.</p></div>`;
    if (!canDo('users:read')) return html`<div class="catalog-empty"><p class="catalog-empty-title">You do not have access to roles</p><p>Ask Root for user administration access.</p></div>`;
    const catalog = roleCatalog.value;
    if (this.error && !catalog) return html`<div class="catalog-empty" role="alert"><p class="catalog-empty-title">Roles could not be loaded</p><p>${this.error}</p></div>`;
    if (!catalog) return html`<div class="catalog-empty" role="status">Loading roles…</div>`;
    return html`<section class="admin-section people roles" aria-busy=${this.loading || this.saving}>
      ${this.renderDefault()}
      <div class="people-table-wrap"><table class="people-table">
        <thead><tr><th scope="col">Role</th><th scope="col">Kind</th><th scope="col">Assigned</th><th scope="col"></th></tr></thead>
        <tbody>${catalog.roles.map((role) => html`
          <tr class=${this.openName === role.name ? 'is-open' : ''}>
            <td><button class="people-name" aria-expanded=${this.openName === role.name} @click=${() => void this.open(role)}>${role.display_name}</button>
              <span class="people-muted">${role.description ?? ''}</span></td>
            <td class="roles-kind"><span class="admin-kind ${role.kind === 'admin' ? 'is-root' : ''}">${KIND_LABEL[role.kind] ?? role.kind}</span></td>
            <td>${plural(role.direct_users, 'person', 'people')}${role.groups ? html`<span class="people-muted">${plural(role.groups, 'group')} · ${plural(role.group_members, 'member')}</span>` : nothing}</td>
            <td><button class="catalog-btn" aria-label=${`Details for ${role.display_name}`} @click=${() => void this.open(role)}>${this.openName === role.name ? 'Close' : 'Details'}</button></td>
          </tr>
          ${this.openName === role.name ? html`<tr><td colspan="4">${this.renderDetail(role)}</td></tr>` : nothing}
        `)}</tbody>
      </table></div>
      <h2 class="roles-heading">What each role allows</h2>
      ${this.renderMatrix()}
    </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'roles-panel': RolesPanel; }
}
